import { useCallback, useEffect, useState } from "react";
import { useSession } from "../../state/SessionProvider";
import { confirm } from "../../components/haptics";
import { erEkteProduksjon } from "../../state/session";
import * as fag from "../../state/fagfunksjon";

/**
 * Hva du er paa produksjonen, og hvilket team du staar paa.
 *
 * ========  DU VELGER DET DU ER, IKKE DET DU FAAR LOV TIL  ========
 *
 * Ingen skal maatte vite hva «crew» betyr for aa komme i gang. Man huker av
 * «A-foto», og tilgangen foelger av det. Rollen SKRIVES ikke herfra — basen
 * utleder den, og det er hele sikkerheten i ordningen.
 *
 * Velger man produksjonsleder, produsent eller redaktoer, staar man som
 * crew til en som alt har noekler bekrefter. Det staar paa skjermen: en
 * person som tror hen er prodleder og ikke er det, moeter en knapp som ikke
 * virker, og da er feilen vaar.
 *
 * Teknikeren slipper gjennom med én gang. Hen har én rett — jammen — og den
 * trengs i sekundet hen staar paa sett.
 *
 * ========  KREVER NETT  ========
 *
 * Dette er oppsett, ikke logging. Appen skal kunne LOGGE i en kjeller; den
 * skal ikke kunne bytte rolle der. Uten nett sier flata det, i stedet for
 * aa vise en tom liste som ser oedelagt ut.
 */
export function FagfunksjonPane(): JSX.Element {
  const { member, production } = useSession();
  const [liste, setListe] = useState<readonly fag.Fagfunksjon[]>([]);
  const [team, setTeam] = useState<readonly fag.Team[]>([]);
  const [mitt, setMitt] = useState<fag.MittValg | null>(null);
  const [henter, setHenter] = useState(true);

  const les = useCallback((): void => {
    void (async () => {
      setMitt(await fag.hentMitt(member.id));
      setHenter(false);
    })();
  }, [member.id]);

  useEffect(() => {
    void (async () => {
      setListe(await fag.hentFagfunksjoner());
      setTeam(await fag.hentTeam(production.id));
    })();
    les();
  }, [production.id, les]);

  if (!erEkteProduksjon(production.id)) {
    return (
      <span className="mono" style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ink-dim)" }}>
        Privat har ingen fagfunksjoner. Du er alene om loggen, og det finnes
        ingen roller å fordele.
      </span>
    );
  }

  if (!henter && liste.length === 0) {
    return (
      <span className="mono" style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ink-dim)" }}>
        Krever nett. Fagfunksjonen settes opp én gang — loggingen virker
        offline som før.
      </span>
    );
  }

  const grupper = [...new Set(liste.map((f) => f.gruppe))];

  return (
    <>
      <span className="mono" style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ink-dim)" }}>
        Velg det du er. Tilgangen følger av valget — alle leser alt i
        produksjonen uansett.
      </span>

      {mitt?.venter === true ? (
        <div className="menu__group">
          <span className="menu__readonly" style={{ color: "var(--tc)" }}>
            Venter på bekreftelse. Du står som crew til en prodleder eller
            produsent slipper deg gjennom.
          </span>
        </div>
      ) : null}

      {team.length === 0 ? null : (
        <div className="menu__group">
          <span className="menu__groupname">TEAM</span>
          {team.map((t) => {
            const valgt = t.id === mitt?.unitId;
            return (
              <button
                key={t.id}
                type="button"
                className="menu__row"
                aria-current={valgt}
                onClick={() => {
                  confirm();
                  void fag.velgTeam(member.id, valgt ? null : t.id).then(les);
                }}
              >
                <span>{t.navn}</span>
                <span className="menu__rowvalue">{valgt ? "✓" : ""}</span>
              </button>
            );
          })}
          <span className="menu__readonly">
            Samler arbeidet ditt med resten av teamet. Det skjuler ingenting.
          </span>
        </div>
      )}

      {grupper.map((g) => (
        <div key={g} className="menu__group">
          <span className="menu__groupname">{g.toUpperCase()}</span>
          {liste.filter((f) => f.gruppe === g).map((f) => {
            const valgt = f.id === mitt?.craftId;
            return (
              <button
                key={f.id}
                type="button"
                className="menu__row"
                aria-current={valgt}
                onClick={() => {
                  if (valgt) return;
                  confirm();
                  void fag.velgFagfunksjon(member.id, f.id).then(les);
                }}
              >
                <span>{f.navn}</span>
                <span className="menu__rowvalue">
                  {valgt ? "✓" : fag.kreverGodkjenning(f.rolle) ? "nøkler" : ""}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}
